/* @flow */
import EventTracking from 'lib/EventTracking';
import FeatureStore from 'stores/FeatureStore';
import Store from 'lib/Store';
import User from 'entities/User';
import UserActions from 'user/UserActions';
import QuickPromotion from 'entities/QuickPromotion';
import { ActionTypes as QuickPromotionActionTypes } from 'constants/QuickPromotionConstants';
import { ActionTypes as AuthActionTypes } from './AuthConstants';
import { ActionTypes as PromptActionTypes } from 'constants/PromptConstants';
import { ActionTypes as ChatSettingsActionTypes } from 'settings/constants/ChatSettingsConstants';
import { PushEvents } from 'pusher/PusherConstants';
import { Roles, ActionTypes as UserActionTypes } from 'user/UserConstants';
import { userInExperimentVariant } from 'lib/Experimentation';
import invariant from 'invariant';
import jwt from 'lib/jsonwebtoken';

class CurrentUser extends Store {
  getInitialState() {
    return {
      user: null,
      token: null,
    };
  }

  addSignInListener(callback: Function) {
    this.on('sign-in', callback);
  }

  removeSignInListener(callback: Function) {
    this.removeListener('sign-in', callback);
  }

  addSignInNoRoleListener(callback: Function) {
    this.on('sign-in-no-role', callback);
  }

  removeSignInNoRoleListener(callback: Function) {
    this.removeListener('sign-in-no-role', callback);
  }

  hasEmail(): boolean {
    return this.state.user && this.state.user.hasEmail();
  }

  getUser(): User {
    invariant(this.isSignedIn(), 'getUser() when not signed in');

    return this.state.user;
  }

  getCreatedAt(): ?Date {
    return this.state.user ? this.state.user.getCreatedAt() : null;
  }

  getUserUuid(): string {
    const user = this.getUser();
    invariant(typeof user.getUuid() === 'string', 'getUserUuId() when user object missing uuid');
    return user.getUuid();
  }

  getUserId(): number {
    const user = this.getUser();
    invariant(typeof user.id === 'number', 'getUserId() when user object missing id');
    return user.id;
  }

  getToken(): ?string {
    return this.state.token;
  }

  getTokenPayload(): ?Object {
    if (this.state.token) {
      return jwt.decode(this.state.token);
    } else {
      return null;
    }
  }

  isSignedIn(): boolean {
    return !!this.state.user;
  }

  // If the user signed in with a token, such as from an unsubscribe link in an email, they will not have the access_token cookie set.
  // All auth'd requests will fail, unless the token is explicitly passed with the request.
  isLimitedUserAuth(): boolean {
    return !!this.state.token;
  }

  getQuickPromotionsForTrigger(trigger: number): QuickPromotion[] {
    const { user } = this.state;
    if (user) {
      return user.getQuickPromotions().where({ trigger });
    } else {
      return [];
    }
  }

  handleUserLoad(user: User): void {
    this.waitFor([FeatureStore]);
    this.state.user = user;
    EventTracking.setUserInfo(this.getUserId(), this.getUser().getUuid());
  }

  /**
   * If checkVariant is omitted, check for a default variant named the same as
   * the web key, but lower case.
   */
  inExpVariant(webKey: string, checkVariant?: string) {
    if (this.isSignedIn()) {
      return userInExperimentVariant(this.getUser(), webKey, checkVariant);
    } else {
      return false;
    }
  }

  emitSigninEvent() {
    const emitEvent = name => {
      setTimeout(() => this.emit(name), 0);
    };

    switch (this.state.user.getRole()) {
      case Roles.parent:
      case Roles.teacher:
      case Roles.student:
        emitEvent('sign-in');
        break;
      default:
        emitEvent('sign-in-no-role');
    }
  }

  handleDispatch(action: Object): void {
    switch (action.type) {
      case AuthActionTypes.TOKEN_ENTRY:
        invariant(action.token, 'missing action.token');
        this.state.token = action.token;
        this.emitChange();
        break;
      case AuthActionTypes.AUTH_SUCCESS:
        invariant(action.user, 'missing action.user');
        this.handleUserLoad(action.user);
        this.emitSigninEvent();
        this.emitChange();
        break;
      case UserActionTypes.LOAD_USER:
        invariant(action.user, 'missing action.user');
        this.handleUserLoad(action.user);
        this.emitChange();
        break;
      case PushEvents.USER:
        invariant(action.eventData && action.model, 'missing action.eventData');
        invariant(typeof action.eventData === 'object', 'invalid action.eventData');
        if (action.eventData.action === 'update') {
          this.state.user = action.model;
          this.emitChange();
        }
        break;
      case UserActionTypes.USER_DELETED:
        /*
          This case will not call 'this.emitChange()' in order to prevent the
          view from updating with a null (deleted) user. After account deletion,
          The user should be redirected to the sign out page, which does not
          require user information.
        */
        this.state.user = null;
        break;
      case UserActionTypes.USER_UPDATED:
        if (this.state.user && action.user && this.state.user.id === action.user.id) {
          this.state.user = action.user;
          this.emitChange();
        }
        break;
      case UserActionTypes.PREFERENCES_UPDATED:
        invariant(action.preferences, 'missing action.preferences');
        this.state.user.setPreferences(action.preferences);
        this.emitChange();
        break;
      case ChatSettingsActionTypes.OFFICE_HOURS_LOADED:
      case ChatSettingsActionTypes.OFFICE_HOURS_ENABLED:
      case ChatSettingsActionTypes.OFFICE_HOURS_DISABLED:
      case ChatSettingsActionTypes.OFFICE_HOURS_UPDATED:
        invariant(action.hours, 'missing action.hours');
        this.state.user.setOfficeHours(action.hours);
        this.emitChange();
        break;
      // Gets called after a prompts inventory prompt is dismissed
      // Prompts inventory prompts are stored in the User object
      case PromptActionTypes.USER_PROMPT_DISMISSED:
        this.emitChange();
        break;
      case PushEvents.CHANGESET_UPDATE_USER:
        UserActions.loadCurrentUser();
        break;
      case QuickPromotionActionTypes.PROMOTION_DISMISSED:
        invariant(action.promotion, 'missing action.promotion');
        this.state.user.removeQuickPromotion(action.promotion);
        this.emitChange();
        break;
      default:
        break;
    }
  }
}

export default new CurrentUser();
